"use client";

import { useEffect, useState } from "react";
import { REGION_CHANGED_EVENT } from "@/components/guide-public/guide-map-engine";
import { RegionLink } from "@/components/guide-public/guide-region-link";
import type { GuideLang } from "@/lib/virtualspace/guide/guide-public-shape";

/** Where each top-level region's marker sits on the drawing (viewBox 600×600). */
const MARKERS: Record<string, { x: number; y: number; labelSide: "below" | "right" | "left" }> = {
  "greater-cairo": { x: 344, y: 108, labelSide: "below" },
  "red-sea": { x: 412, y: 268, labelSide: "right" },
  alexandria: { x: 300, y: 44, labelSide: "right" },
  "north-coast": { x: 220, y: 46, labelSide: "left" },
  "south-sinai": { x: 414, y: 196, labelSide: "right" },
  luxor: { x: 404, y: 360, labelSide: "right" },
  aswan: { x: 418, y: 460, labelSide: "right" },
};

export type OverviewRegion = {
  slug: string;
  label: string;
  count: number;
  /** The real map, focused on this region (`?region=<slug>#map`). */
  href: string;
  /** "3 places · 4 events", for the marker's accessible name. */
  detail?: string;
};

/**
 * An illustrated Egypt: desert, the Nile and its delta, the Red Sea and the
 * pyramids at Giza, with a count of places, events and stories per region.
 * Each marker is a link into the real map below (no reload when it is on
 * the page). A drawing, not a survey: positions are approximate.
 */
export function EgyptOverview({ regions, lang, activeSlug = null }: { regions: OverviewRegion[]; lang: GuideLang; activeSlug?: string | null }) {
  const ar = lang === "ar";
  // The chosen region: from the address at first, then whatever the real map was told.
  const [active, setActive] = useState<string | null>(activeSlug);
  useEffect(() => {
    const onChange = (event: Event) => {
      const slug = (event as CustomEvent<{ slug: string }>).detail.slug;
      setActive(slug === "all" ? null : slug);
    };
    window.addEventListener(REGION_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(REGION_CHANGED_EVENT, onChange);
  }, []);
  const marked = regions.filter((r) => MARKERS[r.slug] && r.count > 0);
  const summary = marked.map((r) => `${r.label}: ${r.count}`).join(", ");
  // A marked region carries its own label; the drawing's place names give way to it.
  const isMarked = (slug: string) => marked.some((r) => r.slug === slug);
  return (
    <svg
      viewBox="0 0 600 600"
      role="img"
      aria-label={ar ? `خريطة مصر: ${summary}` : `Map of Egypt: ${summary}`}
      style={{ display: "block", width: "100%", height: "auto", direction: "ltr" }}
    >
      {/* Neighbouring land, then the seas, then Egypt and Sinai on top. */}
      <rect width="600" height="600" fill="#efe6d3" />
      <path d="M0 0 H600 V34 C520 30 470 28 452 34 L372 38 C350 28 330 26 320 30 C240 40 140 44 60 40 L0 42 Z" fill="#9fb8c6" />
      <path
        d="M372 110 L392 232 L470 360 L560 560 L600 600 V430 L525 300 L452 152 L462 66 L452 66 L440 140 L404 222 Z"
        fill="#9fb8c6"
      />
      {/* Gentle swell on both seas. */}
      <g fill="none" stroke="#7d9db1" strokeWidth="1" opacity="0.55">
        <path d="M40 18 q10 -6 20 0 t20 0 t20 0" />
        <path d="M480 14 q10 -6 20 0 t20 0 t20 0" />
        <path d="M500 480 q8 -6 16 0 t16 0" />
        <path d="M470 400 q8 -6 16 0 t16 0" />
      </g>
      <path
        d="M60 40 C140 44 240 40 320 30 C330 26 350 28 372 38 L372 110 L392 232 L470 360 L560 560 L560 600 L60 600 Z"
        fill="#e4d6bb"
        stroke="#c9bca4"
      />
      <path d="M372 38 L452 34 L440 140 L404 222 L372 110 Z" fill="#e4d6bb" stroke="#c9bca4" />
      {/* The delta's green, the Nile, and the valley's narrow green banks. */}
      <path d="M320 30 L344 104 L366 30 Z" fill="#d6d6a8" opacity="0.7" />
      <path
        d="M326 104 C332 160 356 230 376 300 C392 360 400 420 404 470 L390 600 L418 600 L436 470 C432 420 424 360 408 300 C388 230 366 160 360 104 Z"
        fill="#d6d6a8"
        opacity="0.45"
      />
      <path
        d="M332 32 L344 104 C350 160 372 230 392 300 C408 360 416 420 420 470 L404 600"
        fill="none"
        stroke="#13294b"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path d="M318 30 L344 104 L362 32" fill="none" stroke="#13294b" strokeWidth="1.6" />
      {/* Lake Nasser behind the High Dam. */}
      <path d="M414 476 C420 500 430 540 428 580 L404 600 L400 560 C402 530 406 500 414 476 Z" fill="#9fb8c6" opacity="0.8" />
      <path d="M60 600 H560" stroke="#c9bca4" strokeDasharray="4 6" />
      {isMarked("south-sinai") ? null : (
        <text x="396" y="188" fontFamily="sans-serif" fontSize="11" fill="#5e544a">
          {ar ? "سيناء" : "Sinai"}
        </text>
      )}
      <g fill="none" stroke="#8a6420" strokeWidth="1.5">
        <path d="M300 120 L310 102 L320 120 Z" />
        <path d="M314 120 L321 108 L328 120 Z" />
        <path d="M290 120 L296 110 L302 120 Z" />
      </g>
      <g transform="translate(110 470)" fill="none" stroke="#8a6420" strokeWidth="1.2">
        <circle r="34" />
        <path d="M0 -44 L6 0 L0 44 L-6 0 Z" fill="#8a6420" fillOpacity="0.5" />
        <path d="M-44 0 L0 -6 L44 0 L0 6 Z" />
      </g>
      <text x="110" y="420" textAnchor="middle" fontFamily="Marcellus, serif" fontSize="13" fill="#8a6420">
        N
      </text>
      <text x="120" y="320" fontFamily="Marcellus, serif" fontSize="30" letterSpacing="10" fill="#a8977a">
        EGYPT
      </text>
      <text x="150" y="352" fontFamily="'Reem Kufi', sans-serif" fontSize="24" fill="#a8977a">
        مصر
      </text>
      <text x="505" y="400" fontFamily="Marcellus, serif" fontSize="15" letterSpacing="4" fill="#3e5f75" transform="rotate(60 505 400)">
        {ar ? "البحر الأحمر" : "RED SEA"}
      </text>
      <text x="110" y="24" fontFamily="Marcellus, serif" fontSize="14" letterSpacing="4" fill="#3e5f75">
        {ar ? "البحر المتوسط" : "MEDITERRANEAN SEA"}
      </text>
      <text x="360" y="570" fontFamily="sans-serif" fontSize="10" fill="#5e544a" opacity="0.8">
        {ar ? "بحيرة ناصر" : "Lake Nasser"}
      </text>
      {isMarked("aswan") ? null : (
        <text x="428" y="474" fontFamily="sans-serif" fontSize="12" fill="#5e544a">
          {ar ? "أسوان" : "Aswan"}
        </text>
      )}
      {isMarked("luxor") ? null : (
        <text x="412" y="368" fontFamily="sans-serif" fontSize="12" fill="#5e544a">
          {ar ? "الأقصر" : "Luxor"}
        </text>
      )}
      {marked.map((r) => {
        const m = MARKERS[r.slug];
        const width = Math.max(110, r.label.length * 9 + 36);
        const lx = m.labelSide === "below" ? m.x - width / 2 : m.labelSide === "left" ? m.x - 24 - width : m.x + 24;
        const ly = m.labelSide === "below" ? m.y + 20 : m.y - 16;
        const name = r.detail ? `${r.label}: ${r.detail}` : `${r.label}: ${r.count}`;
        const on = active === r.slug;
        return (
          <RegionLink key={r.slug} slug={r.slug} href={r.href} as="svg" className={`eg-overview-link${on ? " eg-overview-link--on" : ""}`}>
            <title>{name}</title>
            {on ? <circle cx={m.x} cy={m.y} r="26" fill="none" stroke="#d4af37" strokeWidth="2" strokeDasharray="4 4" opacity="0.9" /> : null}
            <circle cx={m.x} cy={m.y} r="17" fill={on ? "#c20000" : "#16120e"} stroke="#d4af37" strokeWidth="2" />
            <text x={m.x} y={m.y + 5} textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="600" fill="#f5f0e6">
              {r.count}
            </text>
            <rect x={lx} y={ly} width={width} height="32" rx="16" fill={on ? "#16120e" : "#fffdf8"} stroke="#16120e" />
            <text
              x={lx + width / 2}
              y={ly + 21}
              textAnchor="middle"
              fontFamily={ar ? "'Reem Kufi', sans-serif" : "Marcellus, serif"}
              fontSize="15"
              fill={on ? "#f5f0e6" : "#16120e"}
            >
              {r.label}
            </text>
          </RegionLink>
        );
      })}
    </svg>
  );
}
